import { useEffect, useRef, useState } from 'react';

export const HEADER_SHRINK_AT = 80;
export const HEADER_EXPAND_AT = 40;

function normalizeThreshold(value, fallback) {
  const cast = Number(value);
  if (!Number.isFinite(cast) || cast < 0) {
    return fallback;
  }
  return cast;
}

function resolveExpandThreshold(shrinkAt, expandAt) {
  const normalizedExpandAt = normalizeThreshold(expandAt, HEADER_EXPAND_AT);
  if (normalizedExpandAt < shrinkAt) {
    return normalizedExpandAt;
  }
  return Math.max(0, shrinkAt - 1);
}

export function resolveHeaderCompactState({
  isCompact,
  scrollY,
  shrinkAt = HEADER_SHRINK_AT,
  expandAt = HEADER_EXPAND_AT,
}) {
  const safeShrinkAt = normalizeThreshold(shrinkAt, HEADER_SHRINK_AT);
  const safeExpandAt = resolveExpandThreshold(safeShrinkAt, expandAt);
  const safeScrollY = Number.isFinite(scrollY) ? scrollY : 0;
  const current = Boolean(isCompact);

  if (!current && safeScrollY > safeShrinkAt) {
    return true;
  }

  if (current && safeScrollY < safeExpandAt) {
    return false;
  }

  return current;
}

export function useHeaderCompact({
  shrinkAt = HEADER_SHRINK_AT,
  expandAt = HEADER_EXPAND_AT,
} = {}) {
  const [isCompact, setIsCompact] = useState(false);
  const compactRef = useRef(isCompact);
  const rafRef = useRef(0);

  useEffect(() => {
    compactRef.current = isCompact;
  }, [isCompact]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateState = () => {
      rafRef.current = 0;

      const nextCompact = resolveHeaderCompactState({
        isCompact: compactRef.current,
        scrollY: window.scrollY,
        shrinkAt,
        expandAt,
      });

      if (nextCompact === compactRef.current) {
        return;
      }

      compactRef.current = nextCompact;
      setIsCompact(nextCompact);
    };

    const onScroll = () => {
      if (rafRef.current) {
        return;
      }

      rafRef.current = window.requestAnimationFrame(updateState);
    };

    updateState();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [expandAt, shrinkAt]);

  return isCompact;
}
